import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBenefitSummary, selectBenefitSummary } from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';

export interface BenefitSummaryLogicReturn {
  // Translated props
  title: string;
  emptyText: string;
  
  // State
  items: any[];
  loading: boolean;
  error: string | null;
  totalAmount: number;
  
  // Redux state
  benefitSummaryData: any;
  
  // Functions
  formatDate: (date: Date) => string;
  formatKoreanDate: (dateString: string) => string;
  
  // Utils
  t: (key: string) => string;
  dispatch: any;
  style: React.CSSProperties;
  className: string;
  isPreview: boolean;
  id: string;
}

/**
 * 날짜 형식화 함수
 */
function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/**
 * 한국어 날짜 형식화 함수
 */
function formatKoreanDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

export const useBenefitSummaryLogic = (props: any): BenefitSummaryLogicReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as any;
  
  // Extract props
  const {
    style = {},
    className = '',
    componentProps = {},
    isPreview = false,
    id = ''
  } = props;

  // Extract component props with translations
  const title = componentProps.title ? t(componentProps.title) : t('최근 1개월 요약');
  const emptyText = componentProps.emptyText ? t(componentProps.emptyText) : t('데이터가 없습니다');

  // Redux store data
  const benefitSummaryData = useSelector(selectBenefitSummary);

  // Component state
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  
  // API call duplicate prevention ref
  const fetchBenefitSummaryRef = useRef(false);

  // Calculate total amount
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  // Fetch benefit summary data on mount
  useEffect(() => {
    // Prevent duplicate API calls
    if (fetchBenefitSummaryRef.current || isPreview) {
      if (isPreview) {
        // Set preview data
        setItems([
          { id: 1, summaryDate: new Date().toISOString(), extraPresetStep: { name: '포인트 적립' }, amount: 5000 },
          { id: 2, summaryDate: new Date(Date.now() - 86400000).toISOString(), extraPresetStep: { name: '쿠폰 사용' }, amount: -1000 },
          { id: 3, summaryDate: new Date(Date.now() - 172800000).toISOString(), extraPresetStep: { name: '이벤트 보상' }, amount: 2000 }
        ]);
        setLoading(false);
      }
      return;
    }
    
    // Calculate date range (30 days ago to now)
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 30);

    const from = formatDate(fromDate);
    const to = formatDate(toDate);

    // Mark API call as started
    fetchBenefitSummaryRef.current = true;

    // Dispatch Redux action to load data
    dispatch(fetchBenefitSummary({ from, to }))
      .unwrap()
      .then(() => {
        setLoading(false);
      })
      .catch((error: any) => {
        console.error("Error fetching benefit summary:", error);
        setLoading(false);
      })
      .finally(() => {
        fetchBenefitSummaryRef.current = false;
      });
  }, [dispatch, isPreview]);

  // Update component state when Redux store data changes
  useEffect(() => {
    if (benefitSummaryData && !isPreview) {
      setItems(benefitSummaryData.items || []);
      setLoading(benefitSummaryData.loading);
    }
  }, [benefitSummaryData, isPreview]);

  return {
    // Translated props
    title,
    emptyText,
    
    // State
    items,
    loading,
    error: benefitSummaryData?.error || null,
    totalAmount,
    
    // Redux state
    benefitSummaryData,
    
    // Functions
    formatDate,
    formatKoreanDate,
    
    // Utils
    t,
    dispatch,
    style,
    className,
    isPreview,
    id
  };
};